import { DiyanetVerse } from '@app-types/index'
import TefsirBody from '@components/shared/Tefsir/TefsirBody'
import TefsirToggle from '@components/shared/Tefsir/TefsirToggle'
import { useState, memo } from 'react'

interface VerseData {
  arabic: {
    text: string
    number: number
    numberInSurah: number
    juz: number
    surah: {
      number: number
      name: string
      englishName: string
      numberOfAyahs: number
    }
  }
  translation: {
    text: string
  }
  tefsir?: DiyanetVerse
}

interface VerseCardProps {
  verse: VerseData | null
  isLoading: boolean
}

const VerseCardSkeleton = () => (
  <div className="bg-white dark:bg-primary-dark-400 rounded-2xl p-5 sacred-shadow animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full" />
      <div className="text-center">
        <div className="h-4 w-24 bg-gray-200 dark:bg-gray-600 rounded mb-1" />
        <div className="h-3 w-16 bg-gray-200 dark:bg-gray-600 rounded" />
      </div>
      <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full" />
    </div>
    <div className="space-y-3 py-6">
      <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-full" />
      <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-3/4 ml-auto" />
    </div>
    <div className="border-t border-gray-100 dark:border-gray-600 mt-4 pt-4 space-y-2">
      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full" />
      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-5/6" />
    </div>
  </div>
)

const VerseCard = memo(({ verse, isLoading }: VerseCardProps) => {
  const [showTefsir, setShowTefsir] = useState(false)

  if (isLoading || !verse) {
    return <VerseCardSkeleton />
  }

  const { arabic, translation } = verse
  const surahName = arabic.surah.englishName
  const verseInSurah = arabic.numberInSurah
  const totalVerses = arabic.surah.numberOfAyahs

  return (
    <div className="bg-main rounded-3xl py-6 px-2 sm:px-4 sacred-shadow border border-main transition-all duration-300 relative z-10">
      <div className="flex items-center justify-center mb-3">
        {/* Surah info */}
        <div className="text-center">
          <h2 className="font-bold text-main text-lg tracking-tight">{surahName}</h2>
          <p className="text-muted text-[10px] font-bold uppercase tracking-widest mt-0.5">
            AYET {verseInSurah} / {totalVerses}
          </p>
        </div>
      </div>

      {/* Arabic text */}
      <div
        className="arab-ver-3 text-right leading-[2.5] py-1 text-main"
        style={{ fontSize: '32px', direction: 'rtl' }}
      >
        {arabic.text}
      </div>

      {/* Translation */}
      <div className="border-t border-main mt-4 pt-6">
        <p className="text-main text-sm sm:text-base leading-relaxed font-medium">
          {verse.tefsir?.meal || translation.text}
        </p>
      </div>

      {/* Tefsir */}
      {verse.tefsir && verse.tefsir.tefsir_html && (
        <div className="border-t border-main mt-4 pt-4">
          <TefsirToggle
            isOpen={showTefsir}
            onToggle={() => setShowTefsir(!showTefsir)}
            verseRange={verse.tefsir.tefsir_range}
          />
          {showTefsir && <TefsirBody html={verse.tefsir.tefsir_html} />}
        </div>
      )}
    </div>
  )
})

VerseCard.displayName = 'VerseCard'

export default VerseCard
